import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = "/api";

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    inquiry_type: "general",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        inquiry_type: "general",
        message: "",
      });
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center py-16 text-center"
      >
        <div className="w-16 h-16 rounded-full bg-[#4A5D23]/20 flex items-center justify-center mb-6">
          <CheckCircle className="w-8 h-8 text-[#8CB369]" />
        </div>
        <h3 className="font-heading font-bold text-white text-2xl uppercase mb-2">
          Message Received
        </h3>
        <p className="text-[#A1A1AA]">
          We'll get back to you as soon as possible.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first_name" className="text-[#A1A1AA] font-mono text-xs uppercase tracking-wider">
            First Name *
          </Label>
          <Input
            id="first_name"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            required
            className="input-tactical input-glow"
            placeholder="John"
            data-testid="contact-first-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name" className="text-[#A1A1AA] font-mono text-xs uppercase tracking-wider">
            Last Name *
          </Label>
          <Input
            id="last_name"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            required
            className="input-tactical input-glow"
            placeholder="Doe"
            data-testid="contact-last-name"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="text-[#A1A1AA] font-mono text-xs uppercase tracking-wider">
          Email *
        </Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="input-tactical input-glow"
          placeholder="john@example.com"
          data-testid="contact-email"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="inquiry_type" className="text-[#A1A1AA] font-mono text-xs uppercase tracking-wider">
          Inquiry Type
        </Label>
        <Select
          value={formData.inquiry_type}
          onValueChange={(value) =>
            setFormData((prev) => ({ ...prev, inquiry_type: value }))
          }
        >
          <SelectTrigger className="input-tactical" data-testid="contact-inquiry-type">
            <SelectValue placeholder="Select inquiry type" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10">
            <SelectItem value="general">General Inquiry</SelectItem>
            <SelectItem value="custom-part">Custom Part Request</SelectItem>
            <SelectItem value="order">Order Question</SelectItem>
            <SelectItem value="partner">Partner Program</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-[#A1A1AA] font-mono text-xs uppercase tracking-wider">
          Message *
        </Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          className="input-tactical input-glow min-h-[150px] resize-none"
          placeholder="Tell us about your project or inquiry..."
          data-testid="contact-message"
        />
      </div>

      <Button
        type="submit"
        className="btn-primary w-full"
        disabled={isSubmitting}
        data-testid="contact-submit"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Send Message
          </>
        )}
      </Button>
    </form>
  );
}
